import { useCallback, useMemo } from "react";
import { styled } from "styled-components";

import arrowRightIcon from "constants/modelCompareData/assets/arrow-right.svg";
import { useModelCompareTranslation } from "constants/modelCompareData/translations/useModelCompareTranslation";
import { imageSrc } from "@ubnt/store.core/lib/utils/asset";
import { StyleableSvgImage } from "@ubnt/store.core/lib/components/StyleableSvgImage";
import { useStickyGrid } from "./StickyGrid/context";

const icon = imageSrc(arrowRightIcon);

export function ModelCompareOverflowScrollButton({
  isVisible,
  color,
  rotationDegrees,
  onClickColumnDiff,
  totalColumns,
  totalDeadSpace,
  isSmallScreen,
  isLeftButton = true,
}: {
  isVisible: boolean;
  color: string;
  rotationDegrees: number;
  onClickColumnDiff: number;
  totalDeadSpace: number;
  totalColumns: number;
  isSmallScreen: boolean;
  isLeftButton?: boolean;
}) {
  const { scrollGroupItemsRef, scrollTo } = useStickyGrid();

  const { formatMessage } = useModelCompareTranslation();

  // Scroll one column on click
  const handleClick = useCallback(() => {
    const anyScrollItem = scrollGroupItemsRef.current?.[0]?.refObject.current;

    if (!anyScrollItem) {
      return;
    }

    const totalWidth = anyScrollItem.scrollWidth;
    const currentScrollLeft = anyScrollItem.scrollLeft + 1;
    const oneColumnWidth = (totalWidth - totalDeadSpace) / totalColumns;
    const currentColumnIndex = Math.min(
      totalColumns - 1,
      Math.max(0, Math.round(currentScrollLeft / oneColumnWidth)),
    );

    const newColumnIndex = Math.max(
      Math.min(currentColumnIndex + onClickColumnDiff, totalColumns),
      0,
    );

    const offsetLeft = newColumnIndex * oneColumnWidth;

    scrollTo({ left: offsetLeft, behavior: "smooth" });
  }, [
    onClickColumnDiff,
    scrollGroupItemsRef,
    scrollTo,
    totalColumns,
    totalDeadSpace,
  ]);

  const arrowOffset = useMemo(() => {
    if (isSmallScreen) {
      return rotationDegrees < 180 ? -5 : 0;
    }

    return -15;
  }, [isSmallScreen, rotationDegrees]);

  return (
    <Button
      aria-label={
        isLeftButton
          ? formatMessage("commons.scrollButton.left")
          : formatMessage("commons.scrollButton.right")
      }
      $color={color}
      $rotation={rotationDegrees}
      $isVisible={isVisible}
      type="button"
      onClick={handleClick}
    >
      <Icon $rotation={rotationDegrees} $arrowOffset={arrowOffset}>
        <StyleableSvgImage width={10} height={16} src={icon} />
      </Icon>
    </Button>
  );
}

const Button = styled.button<{
  $isVisible: boolean;
  $rotation: number;
  $color: string;
}>`
  color: inherit;
  outline: none;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: ${({ $isVisible }) => ($isVisible ? "auto" : "none")};
  transition: opacity 0.2s;
  height: 100%;
  width: 40px;
  background: transparent;
  border: none;
  background-image: linear-gradient(
    ${({ $rotation }) => $rotation - 90}deg,
    ${({ $color }) => $color},
    transparent 20px
  );
  border-left: ${({ $color }) =>
    $color !== "white" ? "1px solid #eee" : "none"};
  cursor: pointer;
`;

const Icon = styled.div<{ $rotation: number; $arrowOffset: number }>`
  transform: rotate(${({ $rotation }) => $rotation}deg)
    translateX(${({ $arrowOffset }) => $arrowOffset ?? 0}px);
  display: flex;
  align-items: center;
  justify-content: center;

  padding-left: 4px;
  height: 44px;
  width: 44px;

  border-radius: 50%;
  background: rgb(255 255 255 / 0.8);
  box-shadow: 0 0 10px 0 rgb(0 0 0 / 0.25);
  backdrop-filter: blur(2px);
  color: #808893;
`;
